import React, { useMemo, useCallback } from "react";
import { CircleAlert, ShareIcon, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useOrg } from "@/hooks/useOrg";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { useCommunityGrowthSeries } from "./hooks/useCommunityGrowthSeries";
import { buildConfig } from "./config";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/Chart";
import { CartesianGrid, LineChart, Line, XAxis, YAxis, Legend } from "recharts";
import { Skeleton } from "../ui/Skeleton";
import { Card, CardContent } from "@/components/ui/Card";
import { useTranslation } from "react-i18next";
import {
  ChartDataPoint,
  combineData,
  buildDateRangeMap,
} from "@/util/chartUtils";
import { ChartHeader } from "../ChartHeader";
import { useShareLink } from "@/hooks/useShareLink";
import { useRoute } from "@/routes/getRoute";
import { getDateString } from "@/util/dateUtils";
import { standardFormatDateShort } from "@/util/stringUtils";
import { subMonths, differenceInDays, addDays } from "date-fns";
import { NavLink } from "react-router-dom";
import { Legend as CustomLegend } from "./Legend";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";

const TIME_SPAN_MONTHS = 6;

/* Smaller homepage version of the growth chart */
export const MiniChart = () => {
  const { org, loading: loadingOrg } = useOrg();
  const { cohortYear, loading: loadingCohortYear } = useCurrentCohortYear();

  const endDate = useMemo(() => new Date(), []);
  const startDate = useMemo(
    () => subMonths(endDate, TIME_SPAN_MONTHS),
    [endDate],
  );

  const {
    data,
    errors,
    loading: loadingChartData,
  } = useCommunityGrowthSeries(cohortYear, org?.id, startDate, endDate);

  const loading = loadingChartData || loadingCohortYear || loadingOrg;

  const chartData = useMemo(() => {
    if (data && cohortYear) {
      const range = buildDateRangeMap(
        getDateString(startDate),
        getDateString(endDate),
      );
      return combineData(
        range,
        Object.values(data).filter((data) => data !== undefined),
      );
    }
  }, [data, cohortYear, startDate, endDate]);

  const chartConfig = buildConfig(data);

  if (errors && errors.length > 0) {
    return (
      <div className="flex h-[282px] w-full flex-col items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <CircleAlert className="h-12 w-12 text-muted-foreground" />
          <div className="text-center text-sm text-muted-foreground">
            {errors.map((error) => {
              return <div key={error.name}>{error.message}</div>;
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <MiniLineChart
      chartData={chartData || []}
      chartConfig={chartConfig}
      loading={loading}
      getTicks={() => getTicks(startDate, endDate)}
    />
  );
};

const getTicks = (startDate: Date, endDate: Date, count = 6): string[] => {
  const dayCount = differenceInDays(endDate, startDate);
  const interval = dayCount / (count - 1);
  const ticks = [];
  for (let start = interval; start < dayCount; start += interval) {
    ticks.push(getDateString(addDays(startDate, start)));
  }
  return [getDateString(startDate), ...ticks, getDateString(endDate)];
};

export function MiniLineChart({
  chartData,
  chartConfig,
  getTicks,
  loading,
}: {
  chartData: ChartDataPoint[];
  chartConfig: ChartConfig;
  getTicks: () => string[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const getRoute = useRoute();
  const { shareLink } = useShareLink();

  const handleExportCSV = useCallback(() => {
    if (!chartData || chartData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    try {
      const cohortYears = Object.keys(chartConfig)
        .filter((key) => !chartConfig[key].hide)
        .sort((a, b) => parseInt(b) - parseInt(a));

      const headers = ["", ...cohortYears];

      const exportData = chartData.map((dataPoint) => {
        const row: Record<string, string | number> = {
          "": new Date(
            (dataPoint.date as string) + "T00:00:00",
          ).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
        };

        cohortYears.forEach((year) => {
          const value = (dataPoint as Record<string, unknown>)[year];
          row[year] = (typeof value === "number" ? value : "") || "";
        });

        return row;
      });

      exportChartCSV({
        filename: timestampFilename("mini-growth-chart"),
        headers,
        data: exportData,
      });

      toast.success(t("global.exportToCSV.success") || "Export successful");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  }, [chartData, chartConfig, t]);

  return (
    <Card className="flex flex-col">
      <ChartHeader
        title={t("home.communityGrowthChart.title")}
        subtitle={t("home.communityGrowthChart.description")}
        menuOptions={[
          {
            label: t("global.exportToCSV.menuTitle"),
            onClick: handleExportCSV,
            icon: <FileDown aria-hidden height={16} />,
          },
          {
            label: t("adoption.communityGrowthChart.menuOptions.share"),
            onClick: () =>
              shareLink(getRoute("adoptionAndEngagement", {}, undefined, true)),
            icon: <ShareIcon aria-hidden height={16} />,
          },
        ]}
      ></ChartHeader>
      <CardContent className="grow">
        {loading ? (
          <Skeleton className="h-[328px] w-full" />
        ) : (
          <NavLink to={getRoute("adoptionAndEngagement")}>
            <ChartContainer className="h-[328px] w-full" config={chartConfig}>
              <LineChart accessibilityLayer data={chartData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  ticks={getTicks()}
                  tickLine={false}
                  axisLine={false}
                  padding={{ left: 15, right: 15 }}
                  tickMargin={7}
                  interval={0}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString("en-US", {
                      month: "short",
                    });
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={50}
                  tickFormatter={(value) => {
                    return new Intl.NumberFormat().format(value);
                  }}
                />
                <ChartTooltip
                  cursor={false}
                  labelFormatter={(label) => (
                    <div>
                      <div>{standardFormatDateShort(label)}</div>
                      <div className="text-muted-foreground">Cohort</div>
                    </div>
                  )}
                  content={
                    <ChartTooltipContent
                      indicator={"line"}
                      nestLabelOverride={false}
                    />
                  }
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  content={(props) => (
                    <CustomLegend chart={props} config={chartConfig} />
                  )}
                />
                {Object.entries(chartConfig).map(([dataKey, chart]) => {
                  return (
                    <Line
                      key={dataKey}
                      dataKey={dataKey}
                      type="linear"
                      stroke={chart.color}
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray={chart.dashed ? "5 5" : undefined}
                    />
                  );
                })}
              </LineChart>
            </ChartContainer>
          </NavLink>
        )}
      </CardContent>
    </Card>
  );
}
